/*
 * Desktop header dropdowns open on hover, site-wide.
 *
 * - Dawn menus (<details> inside .header__inline-menu): opened and closed by
 *   toggling the open attribute; a mouse click on the label no longer closes
 *   a hover-opened menu. Keyboard behaviour (Enter/Space) is unchanged.
 * - Hyper Menu app (US store, li.h2m-main-menu-item): the app is set to open
 *   on click, so hovering an item clicks it open and leaving clicks it shut.
 *
 * A short delay on leaving lets the pointer travel from the label into the
 * panel. Touch devices and keyboard users keep the theme's click behaviour.
 */
(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const CLOSE_DELAY = 180;
  const ITEM = '.header__inline-menu li';
  const timers = new WeakMap();

  // The top-level item for any element inside the menu (labels and panels alike)
  const topItem = (el) => {
    let li = el?.closest?.(ITEM);
    while (li?.parentElement?.closest(ITEM)) li = li.parentElement.closest(ITEM);
    return li;
  };
  const isH2m = (li) => li.classList.contains('h2m-main-menu-item');
  const dropdownOf = (li) => (isH2m(li) ? li.querySelector('.h2m-submenu--root') : li.querySelector(':scope details, :scope > * > details'));

  function setOpen(li, open) {
    if (isH2m(li)) {
      if (li.classList.contains('h2m-menu-active') !== open) li.querySelector('.h2m-menu-item-inner')?.click();
      return;
    }
    const details = dropdownOf(li);
    if (!details || details.open === open) return;
    if (open) details.setAttribute('open', '');
    else details.removeAttribute('open');
    details.querySelector('summary')?.setAttribute('aria-expanded', String(open));
  }

  function closeOthers(current) {
    document.querySelectorAll(ITEM).forEach((li) => {
      if (li === current || !dropdownOf(li) || topItem(li) !== li) return;
      const open = isH2m(li) ? li.classList.contains('h2m-menu-active') : dropdownOf(li).open;
      if (open) setOpen(li, false);
    });
  }

  // Delegated in the capture phase, so items rendered later by apps are
  // covered and scripts that stop pointer events from bubbling don't block it
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const li = topItem(e.target);
    if (!li || !dropdownOf(li)) return;
    clearTimeout(timers.get(li));
    if (li.contains(e.relatedTarget)) return;
    closeOthers(li);
    setOpen(li, true);
  }, true);

  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse') return;
    const li = topItem(e.target);
    if (!li || !dropdownOf(li) || li.contains(e.relatedTarget)) return;
    clearTimeout(timers.get(li));
    timers.set(li, setTimeout(() => setOpen(li, false), CLOSE_DELAY));
  }, true);

  // A mouse click on a Dawn label would close the menu the hover just opened
  document.addEventListener('click', (e) => {
    const summary = e.target.closest?.('.header__inline-menu details > summary');
    if (summary && e.detail > 0 && summary.parentElement.open) e.preventDefault();
  }, true);
})();
